import { useEffect, useState } from "react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import {
  type AffiliateCommissionRow,
  type AffiliateDashboardStatRow,
  fetchDashboardStats,
  fetchMyCommissions,
} from "@/lib/affiliateDashboard";

const DEFAULT_HOLDBACK_DAYS = 30;
const DEFAULT_LAPSE_GRACE_DAYS = 30;

export type PayoutRow = {
  id: string;
  currency: string;
  total_amount_cents: number;
  commission_count: number;
  payment_reference: string | null;
  paid_at: string;
};

export function useAffiliateDashboardData(profileId: string | null, userId: string | undefined) {
  const [stats, setStats] = useState<AffiliateDashboardStatRow[]>([]);
  const [rows, setRows] = useState<AffiliateCommissionRow[]>([]);
  const [payouts, setPayouts] = useState<PayoutRow[]>([]);
  const [loadingData, setLoadingData] = useState(true);
  const [holdbackDays, setHoldbackDays] = useState(DEFAULT_HOLDBACK_DAYS);
  const [lapseGraceDays, setLapseGraceDays] = useState(DEFAULT_LAPSE_GRACE_DAYS);
  const [enrollment, setEnrollment] = useState<{ eligibility_lapsed_at: string | null } | null>(null);

  useEffect(() => {
    void (async () => {
      const { data } = await supabase
        .from("affiliate_program_config")
        .select("holdback_days, lapse_grace_days")
        .maybeSingle();
      if (data) {
        setHoldbackDays(data.holdback_days ?? DEFAULT_HOLDBACK_DAYS);
        setLapseGraceDays(data.lapse_grace_days ?? DEFAULT_LAPSE_GRACE_DAYS);
      }
    })();
  }, []);

  useEffect(() => {
    if (!profileId) return;
    void (async () => {
      const { data } = await supabase
        .from("affiliate_enrollments")
        .select("eligibility_lapsed_at")
        .eq("profile_id", profileId)
        .maybeSingle();
      setEnrollment(data ?? null);
    })();
  }, [profileId]);

  useEffect(() => {
    if (!profileId) return;
    setLoadingData(true);
    void (async () => {
      try {
        const [s, r, p] = await Promise.all([
          fetchDashboardStats(profileId),
          fetchMyCommissions(profileId, { limit: 250 }),
          supabase
            .from("affiliate_payout_batches")
            .select("id, currency, total_amount_cents, commission_count, payment_reference, paid_at")
            .eq("affiliate_user_id", userId ?? "")
            .order("paid_at", { ascending: false })
            .limit(20),
        ]);
        setStats(s);
        setRows(r);
        setPayouts((p.data ?? []) as PayoutRow[]);
      } catch (e: unknown) {
        toast.error(e instanceof Error ? e.message : "Failed to load affiliate data");
      } finally {
        setLoadingData(false);
      }
    })();
  }, [profileId, userId]);

  return { stats, rows, payouts, loadingData, holdbackDays, lapseGraceDays, enrollment };
}
